import type { CaptureResult } from "posthog-js";

// Keep only known action names, never arbitrary event names or payloads.
const ACTIONS = new Set([
  "app_entered", "equipment_count_changed", "equipment_limit_exempt_item_added",
  "local_project_save_succeeded", "local_project_save_failed", "ai_generation_served",
  "cloud_save_attempted", "export_attempted", "export_completed",
]);
const ITEM_KINDS = new Set(["wire", "layout", "busbar", "fuse"]);
const MAX_AGE_MS = 60_000;
const MAX_ACTIONS = 12;

type Breadcrumb = { action: string; at: number; item_kind?: string };

/** In-memory context only; reset with analytics identity. No project data,
 * element text, URLs, event payloads or persistent breadcrumb storage. */
export class ErrorDiagnostics {
  private actions: Breadcrumb[] = [];

  clear(): void { this.actions = []; }

  record(action: string, properties?: Record<string, unknown>, now = Date.now()): void {
    if (!ACTIONS.has(action)) return;
    const kind = properties?.item_kind;
    this.actions = this.actions.filter((entry) => now - entry.at <= MAX_AGE_MS);
    this.actions.push({ action, at: now,
      ...(typeof kind === "string" && ITEM_KINDS.has(kind) ? { item_kind: kind } : {}),
    });
    this.actions = this.actions.slice(-MAX_ACTIONS);
  }

  enrich(event: CaptureResult | null, now = Date.now()): CaptureResult | null {
    if (!event || event.event !== "$exception") return event;
    const exceptions = event.properties.$exception_list;
    if (!Array.isArray(exceptions) || !exceptions.some((entry: unknown) =>
      entry !== null && typeof entry === "object" &&
      (entry as { value?: unknown }).value === "Script error.",
    )) return event;
    // Diagnostics must never interrupt exception delivery, even during teardown.
    try {
      const context: Record<string, unknown> = {
        error_diagnostics_version: 1,
        app_revision: import.meta.env.VITE_APP_REVISION || "unknown",
        recent_actions: this.actions.filter((entry) => now - entry.at <= MAX_AGE_MS)
          .map(({ at, ...entry }) => ({ ...entry, age_ms: Math.max(0, now - at) })),
      };
      if (typeof document !== "undefined") {
        context.page_visibility = document.visibilityState;
        context.rendered_node_count = document.querySelectorAll(".react-flow__node").length;
        context.rendered_wire_count = document.querySelectorAll(".react-flow__edge").length;
        context.dialog_open = Boolean(document.querySelector('[role="dialog"], dialog[open]'));
        // Classify loaded script elements; do not send their URLs (tokens and
        // user-supplied paths may occur even on same-origin scripts).
        context.script_sources = [...new Set(Array.from(document.scripts, (script) => {
          if (!script.src) return "inline";
          const url = new URL(script.src, document.baseURI);
          if (url.origin === location.origin) return "same_origin";
          if (url.hostname === "challenges.cloudflare.com") return "turnstile";
          if (url.hostname === "cdn.paddle.com") return "paddle";
          if (["eu-assets.i.posthog.com", "us-assets.i.posthog.com"].includes(url.hostname)) return "posthog";
          return "other_external";
        }))];
      }
      return { ...event, properties: { ...event.properties, ...context } };
    } catch {
      return event;
    }
  }
}
